// Paid-traffic attribution capture.
//
// Google auto-tagging appends ?gclid=... to the landing URL. A visitor may
// browse several pages before converting, so the click id is captured on first
// sight and kept for the session.
//
// Everything here is runtime-only on purpose: these pages are prerendered by
// react-snap, and any per-visitor value baked into the static HTML would be
// served to every later visitor.

const GCLID_KEY = "rfm_gclid";
const GADS_KEY = "rfm_gads";
const LANDING_KEY = "rfm_landing_page";

const hasWindow = () => typeof window !== "undefined";

// react-snap snapshots the app in a headless browser. Nothing that varies per
// visitor may run during that pass.
const isPrerender = () =>
    hasWindow() &&
    typeof navigator !== "undefined" &&
    navigator.userAgent.includes("ReactSnap");

const readStore = (key) => {
    if (!hasWindow() || isPrerender()) return "";
    try {
        return window.sessionStorage.getItem(key) || "";
    } catch {
        return ""; // private browsing / storage disabled
    }
};

const writeStore = (key, value) => {
    if (!hasWindow() || isPrerender() || !value) return;
    try {
        window.sessionStorage.setItem(key, value);
    } catch {
        /* best effort — attribution must never block a visitor */
    }
};

/**
 * Capture Google Ads click identifiers from the current URL. Safe to call on
 * every route change; it only writes when an identifier is actually present,
 * so an internal navigation cannot clear an earlier capture.
 */
export function captureAttribution() {
    if (!hasWindow() || isPrerender()) return;
    const params = new URLSearchParams(window.location.search);

    // Only a real gclid goes in the gclid slot — Google's offline conversion
    // import treats gclid and wbraid/gbraid as different columns, so mixing
    // them would produce uploads that silently never match.
    const gclid = (params.get("gclid") || "").trim();
    if (gclid) writeStore(GCLID_KEY, gclid.slice(0, 200));

    // wbraid/gbraid are the privacy-safe iOS equivalents. They are not usable
    // as a gclid, but they do prove the visit came from a Google Ads click,
    // which is all the call-tracking swap needs.
    const anyClickId =
        gclid || (params.get("wbraid") || "").trim() || (params.get("gbraid") || "").trim();
    if (anyClickId) {
        writeStore(GADS_KEY, "1");
        writeStore(LANDING_KEY, window.location.pathname);
    }
}

// Read a query parameter from the live URL. The readers below check this before
// falling back to storage so they never depend on captureAttribution() having
// run first: React fires child effects before parent ones, so the header would
// otherwise read an empty store and show the wrong number on the landing hit.
const urlParam = (name) => {
    if (!hasWindow()) return "";
    try {
        return (new URLSearchParams(window.location.search).get(name) || "").trim();
    } catch {
        return "";
    }
};

/** The gclid for this visit, or "" when it did not come from a Google Ads click. */
export function getGclid() {
    return urlParam("gclid").slice(0, 200) || readStore(GCLID_KEY);
}

/** The page the visitor entered on — the ad's real landing page, not /contact. */
export function getLandingPage() {
    return readStore(LANDING_KEY);
}

/** True when this session arrived from a Google Ads click of any kind. */
export function isGoogleAdsVisitor() {
    if (urlParam("gclid") || urlParam("wbraid") || urlParam("gbraid")) return true;
    return readStore(GADS_KEY) === "1";
}
